import { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  Paper,
  CircularProgress,
  Alert,
  Snackbar,
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Divider,
  LinearProgress,
  FormLabel,
  RadioGroup,
  Radio
} from '@mui/material';
import {
  Save as SaveIcon,
  Sync as SyncIcon,
  CheckCircle as CheckCircleIcon,
  ErrorOutline as ErrorOutlineIcon
} from '@mui/icons-material';
import { useQuery, useMutation } from '@tanstack/react-query';
import api from '@/services/auth.service';
import { useAuth } from '@/hooks/useAuth';
import { usePoliticasGlobais, type PoliticasPadrao } from '../hooks/usePoliticasGlobais';
import { DescontoForm } from '../components/DescontoForm';
import { TabelaPrecoForm } from '../components/TabelaPrecoForm';
import { CondicoesPagtoForm } from '../components/CondicoesPagtoForm';
import { DescontosClientesTab } from '../components/DescontosClientesTab';

export function PoliticasGlobaisPage() {
  const { refreshUser } = useAuth();
  const [tab, setTab] = useState(0);
  const [successMsg, setSuccessMsg] = useState('');

  const {
    politicasQuery,
    regrasQuery,
    restricoesPagtoQuery,
    savePoliticasMutation,
    saveRegrasMutation,
    saveRestricoesPagtoMutation
  } = usePoliticasGlobais(tab);

  // AIDEV-PERF: Lookups lazy — só busca quando a aba correspondente está ativa
  const tabelas = useQuery({
    queryKey: ['admin-lookup-tabelas'],
    staleTime: 10 * 60_000,
    enabled: tab === 1,
    queryFn: async () => {
      const res = await api.get<{ tabelas: { codigo: number; descricao: string }[] }>(
        '/admin/lookup/tabelas-preco'
      );
      return res.data.tabelas;
    }
  });

  const condicoes = useQuery({
    queryKey: ['admin-lookup-condicoes'],
    staleTime: 10 * 60_000,
    enabled: tab === 2,
    queryFn: async () => {
      const res = await api.get<{ condicoes: { codigo: number; descricao: string }[] }>(
        '/admin/lookup/condicoes-pagto'
      );
      return res.data.condicoes;
    }
  });

  const origens = useQuery({
    queryKey: ['admin-lookup-origens'],
    staleTime: 10 * 60_000,
    enabled: tab === 3,
    queryFn: async () => {
      const res = await api.get<{ origens: { codigo: number; descricao: string }[] }>(
        '/admin/lookup/origens-pedido'
      );
      return res.data.origens;
    }
  });

  const pontosEstoque = useQuery({
    queryKey: ['admin-lookup-pontos-estoque'],
    staleTime: 10 * 60_000,
    enabled: tab === 3,
    queryFn: async () => {
      const res = await api.get<{ pontos: Array<{ codigo: number; descricao: string }> }>(
        '/admin/lookup/pontos-estoque'
      );
      return res.data.pontos;
    }
  });

  // AIDEV-NOTE: Empresas do ERP p/ o select de empresa padrao (mesma fonte usada no sync do pedido)
  const empresas = useQuery({
    queryKey: ['admin-lookup-empresas'],
    staleTime: 10 * 60_000,
    enabled: tab === 3,
    queryFn: async () => {
      const res = await api.get<{
        empresas: { codigo: number; nome_fantasia: string | null; razao_social: string | null }[];
      }>('/admin/lookup/empresas');
      return res.data.empresas;
    }
  });

  const tiposComercializacao = useQuery({
    queryKey: ['admin-lookup-tipos-comercializacao'],
    staleTime: 10 * 60_000,
    enabled: tab === 3,
    queryFn: async () => {
      const res = await api.get<{ tipos: { codigo: number; descricao: string }[] }>(
        '/admin/lookup/tipos-comercializacao'
      );
      return res.data.tipos;
    }
  });

  const tiposMovimentoReceber = useQuery({
    queryKey: ['admin-lookup-tipos-movimento-receber'],
    staleTime: 10 * 60_000,
    enabled: tab === 3,
    queryFn: async () => {
      const res = await api.get<{ tipos: { codigo: number; descricao: string }[] }>(
        '/admin/lookup/tipos-movimento-receber'
      );
      return res.data.tipos;
    }
  });

  const [politicasLocal, setPoliticasLocal] = useState<Partial<PoliticasPadrao>>({});
  const [regrasLocal, setRegrasLocal] = useState<any[] | null>(null);
  const [restricoesLocal, setRestricoesLocal] = useState<any[] | null>(null);

  const politicas = { ...(politicasQuery.data ?? {}), ...politicasLocal };

  const handleChangePolitica = (field: string, value: any) => {
    setPoliticasLocal((prev) => ({ ...prev, [field]: value }));
  };

  const handleSavePoliticas = async () => {
    await savePoliticasMutation.mutateAsync(politicasLocal);
    setPoliticasLocal({});
    setSuccessMsg('Politicas salvas com sucesso!');
  };

  const handleSaveRegras = async () => {
    await saveRegrasMutation.mutateAsync(regrasLocal ?? []);
    setRegrasLocal(null);
    setSuccessMsg('Regras de tabela salvas!');
  };

  // AIDEV-NOTE: Ao salvar restrições, garante que TODAS as condições tenham um registro explícito.
  // Condições não tocadas pelo admin recebem { ativo: true, valor_minimo_pedido: null }.
  // Sem isso, condições sem registro seriam tratadas como "permitidas" no wizard.
  const handleSaveRestricoes = useCallback(async () => {
    const todasCondicoes = condicoes.data ?? [];
    const editadas = restricoesLocal ?? [];
    const completas = todasCondicoes.map((c) => {
      const existente = editadas.find((r) => r.condicao_pagto_id === c.codigo);
      return existente ?? { condicao_pagto_id: c.codigo, ativo: true, valor_minimo_pedido: null };
    });
    await saveRestricoesPagtoMutation.mutateAsync(completas);
    setRestricoesLocal(null);
    // AIDEV-NOTE: Atualiza o user em memória para que o wizard carregue as restrições frescas
    // sem precisar fazer logout/login. Sem isso, user.restricoes_condicao_pagto_jwt ficaria
    // desatualizado até a próxima recarga da página.
    refreshUser().catch(() => undefined);
    setSuccessMsg('Restrições de pagamento salvas!');
  }, [condicoes.data, restricoesLocal, saveRestricoesPagtoMutation, refreshUser]);

  // --- Sync de Produtos ---
  type SyncPhase = 'idle' | 'syncing' | 'success' | 'error';
  const [syncPhase, setSyncPhase] = useState<SyncPhase>('idle');
  const [syncCount, setSyncCount] = useState<number | null>(null);
  const [syncErrorMsg, setSyncErrorMsg] = useState<string | null>(null);
  // AIDEV-NOTE: triggerTimeRef guarda o ISO timestamp do momento do clique para filtrar logs antigos
  const triggerTimeRef = useRef<string>('');

  interface SyncLogEntry {
    status: 'em_andamento' | 'sucesso' | 'erro';
    iniciado_em: string;
    registros_sucesso: number;
    mensagem_erro: string | null;
  }

  // Polling do último log de produtos — ativo somente enquanto syncPhase === 'syncing'
  const logPollQuery = useQuery({
    queryKey: ['sync-log-poll-produtos'],
    queryFn: async () => {
      const res = await api.get<{ logs: SyncLogEntry[] }>('/sync/logs?entity=produtos&limit=1');
      return res.data.logs?.[0] ?? null;
    },
    enabled: syncPhase === 'syncing',
    refetchInterval: syncPhase === 'syncing' ? 2000 : false,
    refetchOnWindowFocus: false,
    staleTime: 0
  });

  // AIDEV-NOTE: Transição de estado baseada no log — só aceita logs com iniciado_em > triggerTime
  // para não interpretar o último sync periódico como conclusão do sync forçado
  useEffect(() => {
    if (syncPhase !== 'syncing' || !logPollQuery.data) return;
    const log = logPollQuery.data;
    const logTime = new Date(log.iniciado_em).getTime();
    const triggerTime = new Date(triggerTimeRef.current).getTime();
    if (logTime < triggerTime) return;

    if (log.status === 'sucesso') {
      setSyncPhase('success');
      setSyncCount(log.registros_sucesso ?? 0);
    } else if (log.status === 'erro') {
      setSyncPhase('error');
      setSyncErrorMsg(log.mensagem_erro ?? 'Erro desconhecido na sincronização.');
    }
    // 'em_andamento': continua polling
  }, [syncPhase, logPollQuery.data]);

  const forceSyncProdutosMutation = useMutation({
    mutationFn: async () => {
      triggerTimeRef.current = new Date().toISOString();
      await api.post('/sync/trigger', { entity: 'produtos', force: true });
    },
    onSuccess: () => {
      setSyncPhase('syncing');
      setSyncCount(null);
      setSyncErrorMsg(null);
    },
    onError: () => {
      setSyncPhase('error');
      setSyncErrorMsg('Erro ao conectar com o servidor.');
    }
  });

  if (politicasQuery.isLoading) return <CircularProgress />;
  if (politicasQuery.error) return <Alert severity="error">Erro ao carregar politicas</Alert>;

  return (
    <Box>
      <Typography variant="h5" sx={{ mb: 2 }}>
        Politicas Globais
      </Typography>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
        <Tab label="Descontos" />
        <Tab label="Tabela de Preco" />
        <Tab label="Cond. Pagamento" />
        <Tab label="Outras" />
        <Tab label="Sincronização" />
        <Tab label="Desconto por Cliente" />
      </Tabs>

      <Paper sx={{ p: 2 }}>
        {tab === 0 && (
          <>
            <DescontoForm
              title="Descontos (padrao global)"
              podeDescontoItem={politicas.pode_dar_desconto_item ?? false}
              limiteDescontoItem={politicas.limite_desconto_item ?? 0}
              podeDescontoTotal={politicas.pode_dar_desconto_total ?? false}
              limiteDescontoTotal={politicas.limite_desconto_total ?? 0}
              podeAlterarPreco={politicas.pode_alterar_preco_unitario ?? false}
              limiteReducaoPreco={politicas.limite_reducao_preco_pct ?? null}
              limiteAumentoPreco={politicas.limite_aumento_preco_pct ?? null}
              onChange={(field, value) => handleChangePolitica(field, value)}
            />
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSavePoliticas}
                disabled={savePoliticasMutation.isPending}
              >
                {savePoliticasMutation.isPending ? 'Salvando...' : 'Salvar'}
              </Button>
            </Box>
          </>
        )}

        {tab === 1 && (
          <>
            {regrasQuery.isLoading ? (
              <CircularProgress size={20} />
            ) : (
              <TabelaPrecoForm
                regras={regrasLocal ?? regrasQuery.data ?? []}
                tabelas={tabelas.data ?? []}
                onChange={setRegrasLocal}
              />
            )}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveRegras}
                disabled={saveRegrasMutation.isPending || regrasLocal === null}
              >
                Salvar Regras
              </Button>
            </Box>
          </>
        )}

        {tab === 2 && (
          <>
            {restricoesPagtoQuery.isLoading || condicoes.isLoading ? (
              <CircularProgress size={20} />
            ) : (
              <CondicoesPagtoForm
                condicoes={condicoes.data ?? []}
                restricoes={restricoesLocal ?? restricoesPagtoQuery.data ?? []}
                onChange={setRestricoesLocal}
              />
            )}
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSaveRestricoes}
                disabled={saveRestricoesPagtoMutation.isPending || restricoesLocal === null}
              >
                Salvar Restricoes
              </Button>
            </Box>
          </>
        )}

        {tab === 3 && (
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={politicas.pode_selecionar_tabela ?? false}
                  onChange={(e) => handleChangePolitica('pode_selecionar_tabela', e.target.checked)}
                />
              }
              label="Vendedor pode selecionar tabela de preco"
              sx={{ mb: 2, display: 'block' }}
            />
            <FormControl size="small" sx={{ minWidth: 240, mb: 2, display: 'flex' }}>
              <InputLabel>Tabela de Preco Padrao</InputLabel>
              <Select
                value={politicas.tabela_preco_padrao_id ?? ''}
                label="Tabela de Preco Padrao"
                onChange={(e) =>
                  handleChangePolitica(
                    'tabela_preco_padrao_id',
                    e.target.value === '' ? null : Number(e.target.value)
                  )
                }
              >
                <MenuItem value="">Nenhuma</MenuItem>
                {(tabelas.data ?? []).map((t) => (
                  <MenuItem key={t.codigo} value={t.codigo}>
                    {t.codigo} — {t.descricao}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {/* AIDEV-NOTE: Empresa padrao p/ gravar pedidos; vazio = fallback p/ empresa 1 no sync */}
            <FormControl size="small" sx={{ minWidth: 240, mb: 2, display: 'flex' }}>
              <InputLabel>Empresa Padrao (gravacao de pedidos)</InputLabel>
              <Select
                value={politicas.empresa_padrao_id ?? ''}
                label="Empresa Padrao (gravacao de pedidos)"
                onChange={(e) =>
                  handleChangePolitica(
                    'empresa_padrao_id',
                    e.target.value === '' ? null : Number(e.target.value)
                  )
                }
              >
                <MenuItem value="">Empresa 1 (padrao)</MenuItem>
                {(empresas.data ?? []).map((emp) => (
                  <MenuItem key={emp.codigo} value={emp.codigo}>
                    {emp.codigo} —{' '}
                    {emp.nome_fantasia || emp.razao_social || `Empresa ${emp.codigo}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={politicas.pode_cadastrar_clientes ?? false}
                  onChange={(e) =>
                    handleChangePolitica('pode_cadastrar_clientes', e.target.checked)
                  }
                />
              }
              label="Vendedor pode cadastrar clientes"
              sx={{ mb: 2, display: 'block' }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={politicas.pode_alterar_origem_pedido ?? true}
                  onChange={(e) =>
                    handleChangePolitica('pode_alterar_origem_pedido', e.target.checked)
                  }
                />
              }
              label="Vendedor pode alterar origem do pedido"
              sx={{ mb: 2, display: 'block' }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={politicas.pode_alterar_empresa ?? true}
                  onChange={(e) => handleChangePolitica('pode_alterar_empresa', e.target.checked)}
                />
              }
              label="Vendedor pode alterar empresa"
              sx={{ mb: 2, display: 'block' }}
            />
            <FormControl size="small" sx={{ minWidth: 200, mb: 2, display: 'flex' }}>
              <InputLabel>Sync de Pedidos</InputLabel>
              <Select
                value={politicas.config_sync_pedido ?? 'manual'}
                label="Sync de Pedidos"
                onChange={(e) => handleChangePolitica('config_sync_pedido', e.target.value)}
              >
                <MenuItem value="manual">Manual</MenuItem>
                <MenuItem value="automatico">Automatico</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 240, mb: 2, display: 'flex' }}>
              <InputLabel>Origem Padrao do Pedido</InputLabel>
              <Select
                value={politicas.origem_pedido_padrao_id ?? ''}
                label="Origem Padrao do Pedido"
                onChange={(e) =>
                  handleChangePolitica(
                    'origem_pedido_padrao_id',
                    e.target.value === '' ? null : Number(e.target.value)
                  )
                }
              >
                <MenuItem value="">Nenhuma (campo vazio)</MenuItem>
                {(origens.data ?? []).map((o) => (
                  <MenuItem key={o.codigo} value={o.codigo}>
                    {o.descricao}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 240, mb: 2, display: 'flex' }}>
              <InputLabel>Ponto de Estoque</InputLabel>
              <Select
                value={politicas.ponto_estoque_id ?? ''}
                label="Ponto de Estoque"
                onChange={(e) =>
                  handleChangePolitica(
                    'ponto_estoque_id',
                    e.target.value === '' ? null : Number(e.target.value)
                  )
                }
              >
                <MenuItem value="">Todos os pontos</MenuItem>
                {(pontosEstoque.data ?? []).map((p) => (
                  <MenuItem key={p.codigo} value={p.codigo}>
                    {p.descricao}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 240, mb: 2, display: 'flex' }}>
              <InputLabel>Tipo de Comercializacao Padrao</InputLabel>
              <Select
                value={politicas.tipo_comercializacao_padrao_id ?? ''}
                label="Tipo de Comercializacao Padrao"
                onChange={(e) =>
                  handleChangePolitica(
                    'tipo_comercializacao_padrao_id',
                    e.target.value === '' ? null : Number(e.target.value)
                  )
                }
              >
                <MenuItem value="">Nenhum (padrao do ERP)</MenuItem>
                {(tiposComercializacao.data ?? []).map((t) => (
                  <MenuItem key={t.codigo} value={t.codigo}>
                    {t.descricao}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControlLabel
              control={
                <Switch
                  checked={politicas.pode_alterar_tipo_movimento_receber ?? true}
                  onChange={(e) =>
                    handleChangePolitica('pode_alterar_tipo_movimento_receber', e.target.checked)
                  }
                />
              }
              label="Vendedor pode alterar tipo de movimento (receber)"
              sx={{ mb: 2, display: 'block' }}
            />
            <FormControlLabel
              control={
                <Switch
                  checked={politicas.exibir_estoque_vendedor ?? true}
                  onChange={(e) =>
                    handleChangePolitica('exibir_estoque_vendedor', e.target.checked)
                  }
                />
              }
              label="Exibir saldo de estoque para vendedores na grade de produtos"
              sx={{ mb: 2, display: 'block' }}
            />
            <FormControl sx={{ mb: 2 }}>
              <FormLabel>Preço base dos produtos</FormLabel>
              <RadioGroup
                row
                value={politicas.tipo_preco_base ?? 'V'}
                onChange={(e) =>
                  handleChangePolitica('tipo_preco_base', e.target.value as 'V' | 'A')
                }
              >
                <FormControlLabel value="V" control={<Radio />} label="Varejo (PRECO_VENDA)" />
                <FormControlLabel value="A" control={<Radio />} label="Atacado (PRECO_ATACADO)" />
              </RadioGroup>
            </FormControl>
            <FormControl size="small" sx={{ minWidth: 240, mb: 2, display: 'flex' }}>
              <InputLabel>Tipo Movimento Receber Padrao</InputLabel>
              <Select
                value={politicas.tipo_movimento_receber_padrao_id ?? ''}
                label="Tipo Movimento Receber Padrao"
                onChange={(e) =>
                  handleChangePolitica(
                    'tipo_movimento_receber_padrao_id',
                    e.target.value === '' ? null : Number(e.target.value)
                  )
                }
              >
                <MenuItem value="">Nenhum (padrao do ERP)</MenuItem>
                {(tiposMovimentoReceber.data ?? []).map((t) => (
                  <MenuItem key={t.codigo} value={t.codigo}>
                    {t.descricao}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={handleSavePoliticas}
                disabled={savePoliticasMutation.isPending}
              >
                {savePoliticasMutation.isPending ? 'Salvando...' : 'Salvar'}
              </Button>
            </Box>
          </>
        )}
        {tab === 4 && (
          <Box>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              Produtos
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Força a sincronização completa de todos os produtos do ERP, ignorando o delta de
              alterações. Use quando produtos ativos não estão aparecendo no aplicativo.
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {syncPhase === 'idle' && (
              <Button
                variant="outlined"
                startIcon={
                  forceSyncProdutosMutation.isPending ? (
                    <CircularProgress size={16} />
                  ) : (
                    <SyncIcon />
                  )
                }
                onClick={() => forceSyncProdutosMutation.mutate()}
                disabled={forceSyncProdutosMutation.isPending}
              >
                {forceSyncProdutosMutation.isPending
                  ? 'Iniciando...'
                  : 'Forçar sync completo de produtos'}
              </Button>
            )}

            {syncPhase === 'syncing' && (
              <Box sx={{ maxWidth: 420 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Sincronizando todos os produtos do ERP...
                </Typography>
                <LinearProgress />
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ mt: 1, display: 'block' }}
                >
                  Isso pode levar alguns minutos dependendo do volume de produtos.
                </Typography>
              </Box>
            )}

            {syncPhase === 'success' && (
              <Box>
                <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 2, maxWidth: 420 }}>
                  {syncCount != null && syncCount > 0
                    ? `${syncCount.toLocaleString('pt-BR')} produto(s) sincronizado(s) com sucesso.`
                    : 'Sincronização concluída — nenhum produto novo encontrado.'}
                </Alert>
                <Button
                  variant="outlined"
                  startIcon={<SyncIcon />}
                  size="small"
                  onClick={() => setSyncPhase('idle')}
                >
                  Sincronizar novamente
                </Button>
              </Box>
            )}

            {syncPhase === 'error' && (
              <Box>
                <Alert severity="error" icon={<ErrorOutlineIcon />} sx={{ mb: 2, maxWidth: 420 }}>
                  {syncErrorMsg}
                </Alert>
                <Button
                  variant="outlined"
                  startIcon={<SyncIcon />}
                  size="small"
                  onClick={() => setSyncPhase('idle')}
                >
                  Tentar novamente
                </Button>
              </Box>
            )}
          </Box>
        )}

        {tab === 5 && <DescontosClientesTab />}
      </Paper>

      <Snackbar
        open={!!successMsg}
        autoHideDuration={3000}
        onClose={() => setSuccessMsg('')}
        message={successMsg}
      />
    </Box>
  );
}
